import { useRef, useState } from "react";
import * as RadixTooltip from "@radix-ui/react-tooltip";

/**
 * How recently a pointer must have been over the trigger for an open request to be honoured.
 *
 * Generous on purpose: it only has to outlast the gap between a pointer event and Radix's own
 * open decision (which the provider may delay), while being far shorter than the time between
 * a click and a later Tab.
 */
const POINTER_GRACE_MS = 1_000;

/**
 * The application's one tooltip. **Never use the native `title` attribute for user-facing
 * hints** — it is unstyled, invisible on touch, waits a second on a meeting-room pointer, and
 * cannot be translated-tested. This one opens **on hover only** (see `onOpenChange` for why
 * focus was deliberately excluded), portals above dialogs, and takes catalogue text like
 * everything else.
 *
 * The trigger must be a focusable element (`asChild` puts the handlers on it directly). An
 * icon-only control still needs its own `aria-label` — a tooltip names on demand; a label
 * names always.
 *
 * `TooltipProvider` is mounted once in `App`, which is what makes moving between adjacent
 * tooltips instant instead of re-waiting the delay.
 */
export function Tooltip({
  content,
  children,
}: {
  content: React.ReactNode;
  children: React.ReactElement;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  /**
   * When a pointer was last over this trigger.
   *
   * A timestamp rather than a boolean flag, because a boolean depends on **whose handler runs
   * first** — Radix composes its own pointer handlers with the ones passed here, and with a
   * zero open-delay it asked to open before the flag had been set, so the tooltip never opened
   * at all. A recency check cannot be raced: hovering necessarily delivers a pointer event to
   * the trigger before Radix decides to open, and focus arriving on its own delivers none.
   */
  const lastPointerAt = useRef(0);

  return (
    <RadixTooltip.Root
      /**
       * No grace area between trigger and content: these tooltips are a few words of text that
       * nobody needs to move the pointer *into*. Radix's hoverable-content logic exists for
       * tooltips containing links, and it computes an exit side from the trigger's rectangle —
       * which threw `unreachable` in jsdom, where every rectangle is zero-sized. Turning it off
       * removes machinery this app has no use for and a crash with it.
       */
      disableHoverableContent
      open={open}
      onOpenChange={(next) => {
        /**
         * **Hover opens a tooltip. Focus does not.**
         *
         * Radix opens on focus as well as hover, and that produced a real defect: one Tab into
         * an open detail modal popped the "Delete opportunity" tooltip with the pointer nowhere
         * near it — measured, not guessed — and the same happened whenever a dialog restored
         * focus to the control that opened it. A popup nobody asked for reads as a bug.
         *
         * Nothing is lost by refusing it. Every icon-only control in this app carries an
         * `aria-label` with the same words as its tooltip (the tooltip guideline in
         * ui-patterns.md requires both), so a screen reader already announces it on focus and a
         * sighted keyboard user is not being told something the label does not say. Where a
         * tooltip ever carries *more* than its control's name, that is the case to reconsider
         * this for — deliberately, and for that control.
         */
        if (next && Date.now() - lastPointerAt.current > POINTER_GRACE_MS) return;
        setOpen(next);
      }}
    >
      <RadixTooltip.Trigger
        asChild
        onPointerEnter={() => {
          lastPointerAt.current = Date.now();
        }}
        onPointerMove={() => {
          lastPointerAt.current = Date.now();
        }}
        onPointerLeave={() => {
          lastPointerAt.current = 0;
          setOpen(false);
        }}
      >
        {children}
      </RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          sideOffset={6}
          className="bg-popover text-popover-foreground z-50 rounded-md border px-2 py-1 text-xs shadow-md"
        >
          {content}
          <RadixTooltip.Arrow className="fill-popover" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}

export const TooltipProvider = RadixTooltip.Provider;
